import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Autocomplete, TextField, Chip, alpha, Tooltip,
} from '@mui/material';
import moment from 'moment';
import { Iconify } from '..';
import { dispatch } from '../../redux/store';
import { getExcludableCampaigns } from '../../redux/slices/audienceManager';

const CHANNEL_COLORS = {
  WHATSAPP: '#25D366',
  EMAIL: '#0EA5E9',
  SMS: '#6366F1',
};

/**
 * Multi-select picker for "exclude recipients from these campaigns".
 *
 * Fetches recent (last 30 days) sent campaigns on the current practice
 * and renders them as an MUI Autocomplete. Selected campaign pks are
 * surfaced via the ``onChange`` callback as a number[] so the parent
 * audience builder can stamp them onto the audience's
 * ``exclude_received_in_campaigns`` field on save.
 *
 * Live: the backend re-evaluates the exclusion every time the audience
 * is used (preview count, send). New deliveries to those campaigns
 * shrink this audience over time.
 */
export default function CampaignExcludePicker({ value, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(
      getExcludableCampaigns((items) => {
        setOptions(items || []);
        setLoading(false);
      })
    );
  }, []);

  // Hydrate any saved pk that isn't in the recent-30-day options
  // (campaign older than the window, or deleted) as a fallback chip
  // labeled by id. Without this, an "out of window" pk lives in state
  // but renders no chip — the user can't see, edit, or remove it, and
  // it silently saves through unchanged on next submit. Better UX: show
  // a neutral chip so the operator can deliberately keep or drop it.
  const knownById = new Map(options.map((o) => [o.id, o]));
  const selectedItems = (value || []).map((id) =>
    knownById.get(id) || { id, name: `Campaign #${id}`, channel: null, status: '', sent_count: 0, scheduled_at: null, _stale: true }
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Iconify icon="solar:user-minus-bold" width={18} sx={{ color: '#64748b' }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>
          Exclude recipients from previous campaigns
        </Typography>
        <Tooltip
          title="Leads who already received a message from a campaign you pick here will be excluded from this audience — live, on every use. Useful for sending a follow-up only to people who DIDN'T get the previous send."
          arrow
        >
          <Box sx={{ display: 'inline-flex', cursor: 'help' }}>
            <Iconify icon="solar:info-circle-bold" width={14} sx={{ color: '#94a3b8' }} />
          </Box>
        </Tooltip>
      </Box>

      <Autocomplete
        multiple
        loading={loading}
        options={options}
        value={selectedItems}
        onChange={(_, newSelected) => onChange((newSelected || []).map((s) => s.id))}
        getOptionLabel={(o) => o.name || `Campaign ${o.id}`}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        size="small"
        noOptionsText={loading ? 'Loading…' : 'No recent sent campaigns to choose from'}
        renderTags={(items, getTagProps) =>
          items.map((opt, idx) => {
            const color = CHANNEL_COLORS[opt.channel] || '#64748b';
            return (
              <Chip
                {...getTagProps({ index: idx })}
                key={opt.id}
                size="small"
                label={`${opt.name} (${(opt.sent_count ?? 0).toLocaleString()})`}
                sx={{
                  bgcolor: alpha(color, 0.08),
                  color,
                  border: `1px solid ${alpha(color, 0.24)}`,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              />
            );
          })
        }
        renderOption={(props, opt) => {
          const color = CHANNEL_COLORS[opt.channel] || '#64748b';
          const when = opt.scheduled_at ? moment(opt.scheduled_at).format('D MMM YYYY') : '—';
          return (
            <li {...props} key={opt.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Chip
                  size="small"
                  label={opt.channel}
                  sx={{
                    bgcolor: alpha(color, 0.1),
                    color,
                    fontSize: 10,
                    fontWeight: 700,
                    minWidth: 60,
                    height: 18,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }} noWrap>
                    {opt.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                    {when} · {(opt.sent_count ?? 0).toLocaleString()} sent · {(opt.status || '').toLowerCase()}
                  </Typography>
                </Box>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              selectedItems.length
                ? 'Add another campaign…'
                : 'Pick recently-sent campaigns to exclude their recipients'
            }
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
          />
        )}
        sx={{ width: '100%' }}
      />

      {selectedItems.length > 0 && (
        <Typography sx={{ fontSize: 11, color: '#64748b', mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Iconify icon="solar:info-circle-bold" width={12} />
          Recipients from {selectedItems.length} campaign{selectedItems.length === 1 ? '' : 's'} will be removed at audience-use time. The exclusion is live — if more recipients get the message later, they're excluded too.
        </Typography>
      )}
    </Box>
  );
}

CampaignExcludePicker.propTypes = {
  /** Array of campaign pks currently selected for exclusion */
  value: PropTypes.arrayOf(PropTypes.number),
  /** Called with the new pk[] when the user adds/removes a campaign */
  onChange: PropTypes.func.isRequired,
};

CampaignExcludePicker.defaultProps = {
  value: [],
};
